import React, { createContext, useContext, ReactNode, useMemo } from 'react'
import { useGetProfile } from '@core/react-query/features/profile/hooks/useGetProfile'
import useFeatureFlags from '@core/providers/useFeatureFlags'

export interface AccessPermissions {
  isViewMode: boolean
  // Add more permissions here as needed in the future
  // isSuperUser: boolean
  // canEditProjects: boolean
  // etc.
}

type AccessContextValue = AccessPermissions

// Create context for access permissions
const AccessContext = createContext<AccessContextValue | null>(null)

interface AccessProviderProps {
  children: ReactNode
}

/**
 * Access Provider
 *
 * This provider:
 * 1. Fetches the user profile using useGetProfile
 * 2. Extracts and manages user permissions
 * 3. Provides access permissions to the entire app via useProductAccess hook
 */
export const AccessProvider: React.FC<AccessProviderProps> = ({ children }) => {
  const { flags } = useFeatureFlags(['web_kiosk_mode'])
  const { data: user } = useGetProfile({
    enabled: false, // Profile is already fetched higher up in the component chain
  })

  const permissions = useMemo<AccessPermissions>(() => {
    if (!user) {
      return {
        isViewMode: false,
        // Add default values for future permissions
      }
    }

    return {
      isViewMode: !!flags.web_kiosk_mode && user.isKioskUser(),
      // Add more permission checks here as needed
      // isSuperUser: user.isSuperUser(),
      // canEditProjects: user.canEditProjects(),
    }
  }, [user, flags.web_kiosk_mode])

  return (
    <AccessContext.Provider value={permissions}>
      {children}
    </AccessContext.Provider>
  )
}

/**
 * Hook to use the access permissions context
 *
 * @returns The access permissions
 *
 * @example
 * const permissions = useProductAccess()
 *
 * if (permissions.isKioskUser) {
 *   // Show kiosk-specific UI
 * }
 */
export const useProductAccess = (): AccessPermissions => {
  const context = useContext(AccessContext)

  if (!context) {
    throw new Error('useProductAccess must be used within an AccessProvider')
  }

  return context
}
